import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { choose } from "lit/directives/choose.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { until } from "lit/directives/until.js";
import { IMedia } from "../../models/Models";
import { watch } from "../internal/watch";
import { getMediaTypeFromMimeType, MediaType } from "../../Media";
import { BaseElement } from "../BaseElement";
import { isValidUrl } from "../../Utils";

import { styles } from "./Media.style";
import { litIf } from "../LitUtils";

import "../GeoViewer";
import "../ProfileBadge/ProfileBadge";

import { getModuleLog } from "../../Logger";

enum AttributionType {
    None,
    TextOnly,
    LinkOnly,
    LinkAndText,
    UserProfile,
}

export type MediaSize = "fullsize" | "thumbnail_1024px" | "thumbnail_512px" | "thumbnail_256px";

const MEDIA_ORDER = ["thumbnail_256px", "thumbnail_512px", "thumbnail_1024px", "fullsize"];
const MEDIA_SIZE_TO_PROP = ["thumbnail_256px", "thumbnail_512px", "thumbnail_1024px", "file"];

@customElement("ds-media")
export class Media extends BaseElement {
    static override styles = [styles];

    @property({ type: Object }) media?: IMedia;
    @property({ type: Boolean }) noHeader = false;
    @property({ type: Boolean }) hideAttribution = false;
    @property({ type: Boolean }) showEditControls = false;
    @property({ type: Boolean }) hideCloseButton = false;
    @property({ type: String }) size: MediaSize = "fullsize";

    @state() protected _url = "";
    @state() protected _type = MediaType.Unknown;
    @state() protected _attribution?: string | null;
    @state() protected _attributionLink?: string | null;
    @state() protected _attributionType = AttributionType.None;
    @state() protected _loading?: any;
    @state() protected _description?: string;
    @state() protected _noHeader = false;
    @state() protected _hideAttribution = false;

    private _log = getModuleLog("media");

    @watch(["media", "size", "hideAttribution", "showEditControls", "hideCloseButton", "noHeader"])
    async _updated() {
        this._url = "";
        this._type = MediaType.Unknown;
        this._attribution = undefined;
        this._attributionLink = undefined;
        this._attributionType = AttributionType.None;
        this._description = undefined;
        this._noHeader = false;

        if (this.media) {
            this._type = getMediaTypeFromMimeType(this.media.mimeType);

            if (this.hideAttribution) {
                this._hideAttribution = true;
            } else {
                this._attribution = this.media.attribution;
                this._attributionLink = this.media.attributionLink;

                if (this.media.contributorId) {
                    this._attributionType = AttributionType.UserProfile;
                } else if (this._attribution && this._attributionLink) {
                    this._attributionType = AttributionType.LinkAndText;
                } else if (this._attribution) {
                    this._attributionType = AttributionType.TextOnly;
                } else if (this._attributionLink) {
                    this._attributionType = AttributionType.LinkOnly;
                } else {
                    if (!this.showEditControls && this.hideCloseButton) {
                        this._hideAttribution = true;
                    }
                }
            }

            if (!!this.media.description && this.media.description.length > 0) {
                this._description = this.media.description;
            } else {
                this._description = this.media.name ?? undefined;
            }

            this._noHeader =
                this.noHeader ||
                (this._hideAttribution &&
                    this.hideCloseButton &&
                    !this.showEditControls &&
                    !this._description);
            this._loading = this._load();
        }
    }

    private async _load() {
        // Check if it is already a valid URL as we maybe displaying an image that is
        // yet to be uploaded
        if (this.media) {
            const media = this.media;
            if (isValidUrl(media.file.key)) {
                this._url = media.file.key;
            } else {
                // search for the closest media to the requested size
                let sizeIndex = MEDIA_ORDER.indexOf(this.size);

                while (sizeIndex < MEDIA_ORDER.length) {
                    const file = (media as any)[MEDIA_SIZE_TO_PROP[sizeIndex]];
                    if (file) {
                        const blob = await this._kernel.mediaLibrary.getCached(file.key);
                        if (blob) {
                            this._url = URL.createObjectURL(blob);
                            this._type = getMediaTypeFromMimeType(media.mimeType);
                        } else {
                            this._url = `https://media.cavedb.net/${file.key}`;
                        }

                        if (MEDIA_ORDER.indexOf(this.size) !== sizeIndex) {
                            this._log.trace(
                                `Wanted ${this.size} but used ${MEDIA_ORDER[sizeIndex]}`
                            );
                        } else {
                            this._log.trace(`Used ${MEDIA_ORDER[sizeIndex]}`);
                        }
                        break;
                    }
                    sizeIndex++;
                }
            }
        }

        if (!this._url) {
            return html`<div class="loading"></div>`;
        } else {
            return html`
                ${choose(this._type, [
                    [MediaType.Image, () => html`<img part="image" src=${this._url} />`],
                    [MediaType.Svg, () => html`<img part="image" src=${this._url} />`],
                    [
                        MediaType.Video,
                        () =>
                            html`<div class="video-wrapper">
                                <video
                                    part="video"
                                    controls
                                    autoplay
                                    muted
                                    src=${this._url}
                                    type=${ifDefined(this.media?.mimeType)}
                                ></video>
                            </div>`,
                    ],
                    [
                        MediaType.Pdf,
                        () =>
                            html`<div class="video-wrapper">
                                <object
                                    part="pdf"
                                    data=${this._url}
                                    type="application/pdf"
                                    width="100%"
                                    height="100%"
                                >
                                    <p>
                                        Open PDF file
                                        <a
                                            href=${this._url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            >here</a
                                        >
                                        <embed src=${this._url} type="application/pdf" />
                                    </p>
                                </object>
                            </div>`,
                    ],
                    [
                        MediaType.Gpx,
                        () =>
                            html`<div class="geo-wrapper">
                                <ds-geo-viewer
                                    part="geo"
                                    type="gpx"
                                    .src=${this._url}
                                ></ds-geo-viewer>
                            </div>`,
                    ],
                    [
                        MediaType.Kml,
                        () =>
                            html`<div class="geo-wrapper">
                                <ds-geo-viewer
                                    part="geo"
                                    type="kml"
                                    .src=${this._url}
                                ></ds-geo-viewer>
                            </div>`,
                    ],
                    [
                        MediaType.GeoJson,
                        () =>
                            html`<div class="geo-wrapper">
                                <ds-geo-viewer
                                    part="geo"
                                    type="geojson"
                                    .src=${this._url}
                                ></ds-geo-viewer>
                            </div>`,
                    ],
                    [
                        MediaType.YouTube,
                        () =>
                            html`<iframe
                                part="youtube"
                                width="100%"
                                height="100%"
                                src=${this._url}
                                title="YouTube video player"
                                frameborder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowfullscreen
                            ></iframe>`,
                    ],
                ])}
            `;
        }
    }

    private _renderLoading() {
        return html`<div class="loading"><sl-spinner></sl-spinner></div>`;
    }

    private _renderAttribution() {
        return litIf(
            !this._hideAttribution,
            () =>
                html`<div class="flex-row">
                    ${choose(this._attributionType, [
                        [AttributionType.None, () => html``],
                        [
                            AttributionType.UserProfile,
                            () =>
                                html`<ds-profile-badge
                                    .user=${this.media?.contributorId as string}
                                ></ds-profile-badge>`,
                        ],

                        [
                            AttributionType.LinkAndText,
                            () =>
                                html`<a
                                    class="attribution"
                                    target="_blank"
                                    href=${this._attributionLink as string}
                                    >By ${this._attribution}</a
                                >`,
                        ],
                        [
                            AttributionType.TextOnly,
                            () => html`<span class="attribution">By ${this._attribution}</span>`,
                        ],
                        [
                            AttributionType.LinkOnly,
                            () =>
                                html`<a
                                    class="attribution"
                                    target="_blank"
                                    href=${this._attributionLink as string}
                                    >${this._attributionLink}</a
                                >`,
                        ],
                    ])}
                </div>`
        );
    }

    private _renderHeader() {
        return litIf(
            !this._noHeader,
            () =>
                html` <div class="header flex-row">
                    <div class="flex-row attribution-container">
                        ${this._renderAttribution()}
                        ${litIf(
                            this._description,
                            () =>
                                html`<div
                                    class="description ${this._attributionType ===
                                    AttributionType.None
                                        ? ""
                                        : "border-left"}"
                                >
                                    <span>${this._description}</span>
                                </div>`
                        )}
                    </div>
                    <div class="flex-row controls">
                        ${litIf(
                            this.showEditControls,
                            () => html`
                                <sl-icon-button
                                    name="trash3"
                                    @click=${() =>
                                        this._raiseEvent("ds-delete", {
                                            media: this.media,
                                        })}
                                ></sl-icon-button>
                                <sl-icon-button
                                    name="pencil-fill"
                                    @click=${() =>
                                        this._raiseEvent("ds-edit", {
                                            media: this.media,
                                        })}
                                ></sl-icon-button>
                            `
                        )}
                        ${litIf(
                            !this.hideCloseButton,
                            () => html`<ds-close-button></ds-close-button>`
                        )}
                    </div>
                </div>`
        );
    }

    override render() {
        return html`<div
            class="container flex-column ${this._hideAttribution ? "no-attribution" : ""}"
        >
            ${this._renderHeader()}

            <div
                class="body"
                @click=${() =>
                    this._raiseEvent("ds-media-click", {
                        media: this.media,
                    })}
            >
                ${until(this._loading, this._renderLoading())}
            </div>
        </div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-media": Media;
    }
}
